import { acquireAccessToken } from 'core/services/ApiService';

/**
 * Handles exporting data to Excel format using the backend API
 * @param msalInstance - The MSAL instance for acquiring the access token
 */
export async function exportExcel(msalInstance: any): Promise<void> {
  // Get the current URL to extract report ID and filters
  const currentUrl = window.location.href;
  const urlObj = new URL(currentUrl);

  // Extract the report ID from the path
  const pathParts = urlObj.pathname.split('/');
  const reportIndex = pathParts.indexOf('grid') + 1;

  if (reportIndex <= 0 || reportIndex >= pathParts.length) {
    throw new Error('Could not determine report ID from URL');
  }

  const reportId = pathParts[reportIndex];

  // Get the current filter and order parameters
  const filterParam = urlObj.searchParams.get('filter') || '';
  const orderParam = urlObj.searchParams.get('order') || '';

  // Get authentication token
  const token = await acquireAccessToken(msalInstance);

  if (!token) {
    throw new Error('No authentication token available');
  }

  // Use relative URL to avoid CORS issues
  const projectKey = 'fmsr';
  const exportUrl = `/api/v1/projects/${projectKey}/reports/${reportId}/export/excel`;

  // Prepare query parameters
  const params = new URLSearchParams();
  if (filterParam) params.append('filter', filterParam);
  if (orderParam) params.append('order', orderParam);

  const queryString = params.toString();
  const fullUrl = `${exportUrl}${queryString ? `?${queryString}` : ''}`;

  // Keep the browser timeout above the connector's two-minute export timeout.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 180000);

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    });
  } catch (error: any) {
    if (error.name === 'AbortError') {
      throw new Error('Export request timed out after 3 minutes');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(`Export failed with status: ${response.status}`);
  }

  // Get the blob from the response
  const blob = await response.blob();

  // Create a download link and trigger it
  const downloadUrl = window.URL.createObjectURL(blob);
  const filename = `${reportId}_export.xlsx`;

  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL
  window.URL.revokeObjectURL(downloadUrl);
}
